/**
 * Centralized week calculation utilities
 * Ensures consistent Monday-to-Sunday weeks across the entire application
 */

export interface WeekRange {
  start: Date;
  end: Date;
  startString: string;
  endString: string;
}

/**
 * Gets the Monday-to-Sunday week that contains the given date
 * This is the SINGLE source of truth for week calculations
 */
export function getWeekRange(date: Date): WeekRange {
  // Clone the input date to avoid mutation
  const inputDate = new Date(date);
  const dayOfWeek = inputDate.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  
  // Calculate days to subtract to get to Monday
  let daysToMonday: number;
  if (dayOfWeek === 0) { // Sunday
    daysToMonday = 6; // Go back 6 days to get to Monday
  } else { // Monday through Saturday  
    daysToMonday = dayOfWeek - 1; // Monday=0 days back, Tuesday=1 day back, etc.
  }
  
  // Calculate Monday (week start)
  const monday = new Date(inputDate);
  monday.setDate(inputDate.getDate() - daysToMonday);
  
  // Calculate Sunday (week end)  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  // Use local date string instead of ISO to avoid timezone shifting
  const startString = monday.getFullYear() + '-' + 
                     String(monday.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(monday.getDate()).padStart(2, '0');
  
  const endString = sunday.getFullYear() + '-' + 
                   String(sunday.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(sunday.getDate()).padStart(2, '0');

  return {
    start: monday,
    end: sunday,
    startString: startString,
    endString: endString
  };
}

/**
 * Gets an array of dates for the week (Monday through Sunday)
 */
export function getWeekDates(referenceDate: Date = new Date()): Date[] {
  const weekRange = getWeekRange(referenceDate);
  const dates: Date[] = [];
  
  // Use string-based date construction to avoid timezone shifting
  const startYear = parseInt(weekRange.startString.split('-')[0]);
  const startMonth = parseInt(weekRange.startString.split('-')[1]) - 1; // Month is 0-based
  const startDay = parseInt(weekRange.startString.split('-')[2]);
  
  for (let i = 0; i < 7; i++) {
    // Create date using local time constructor to avoid timezone shifts
    const date = new Date(startYear, startMonth, startDay + i);
    dates.push(date);
  }
  
  // Verify the fix worked - only log errors
  const firstDateString = dates[0].getFullYear() + '-' + 
                         String(dates[0].getMonth() + 1).padStart(2, '0') + '-' + 
                         String(dates[0].getDate()).padStart(2, '0');
  
  if (firstDateString !== weekRange.startString) {
    console.error('❌ [WeekUtils] TIMEZONE FIX FAILED - First date mismatch:', {
      expected: weekRange.startString,
      actual: firstDateString
    });
  }
  
  return dates;
} 